import { FormControl, MenuItem, Select, SelectChangeEvent, SxProps, Theme } from "@mui/material";
import { ReactNode } from "react";

export type ApplicationSelectOption = {
    value: string;
    label: string;
    content?: ReactNode;
    disabled?: boolean;
};

type ApplicationSelectProps = {
    id?: string;
    value: string;
    options: ApplicationSelectOption[];
    onChange: (value: string) => void;
    width?: number | string;
    menuWidth?: number | string;
    menuMaxWidth?: number | string;
    disabled?: boolean;
    displayEmpty?: boolean;
    fallbackLabel?: string;
    fontSize?: number | string;
    fontWeight?: number | string;
    padding?: string;
    menuItemJustifyContent?: string;
    menuItemTextAlign?: string;
    menuItemPaddingY?: number;
    renderValue?: (value: string) => ReactNode;
    formControlSx?: SxProps<Theme>;
    sx?: SxProps<Theme>;
    menuItemSx?: SxProps<Theme>;
};

export function ApplicationSelect({
    id,
    value,
    options,
    onChange,
    width = '100%',
    menuWidth,
    menuMaxWidth,
    disabled = false,
    displayEmpty = false,
    fallbackLabel = '',
    fontSize = '15px',
    fontWeight = undefined,
    padding = '5px 0px 0px 4px',
    menuItemJustifyContent = 'flex-start',
    menuItemTextAlign = 'left',
    menuItemPaddingY = 0.25,
    renderValue,
    formControlSx = undefined,
    sx = undefined,
    menuItemSx = undefined,
}: ApplicationSelectProps) {
    const selectSx: SxProps<Theme> = [
        {
            width: width,
            '.MuiFormLabel-root': {
                fontWeight: 'bold'
            },
            '&.Mui-focused .MuiSelect-select': {
                backgroundColor: 'transparent',
            },
            '&:before, &:after': {
                borderBottom: 'none',
            },
            '&.MuiInput-underline.Mui-disabled:before': {
                borderBottom: 'none',
            },
            '&:hover:not(.Mui-disabled, .Mui-error):before': {
                borderBottom: 'none',
            },
            margin: '0px 0px 0px 0px',
            borderColor: '#36454B',
            borderRadius: '4px',
            borderStyle: 'solid',
            borderWidth: '1px',
            color: '#36454B',
            '& .MuiSvgIcon-root': {
                fontSize: '24px',
            },
            fontSize: fontSize,
            fontWeight: fontWeight,
            height: '24px',
            padding: padding,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ];

    const defaultMenuItemSx: SxProps<Theme> = [
        {
            fontSize: 14,
            paddingY: menuItemPaddingY,
            paddingX: 1,
            minHeight: 32,
            justifyContent: menuItemJustifyContent,
            textAlign: menuItemTextAlign,

            '&.Mui-selected': {
                backgroundColor: 'rgba(0,0,0,0.08)',
            },
            '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
            }
        },
        ...(Array.isArray(menuItemSx) ? menuItemSx : menuItemSx ? [menuItemSx] : []),
    ];

    const handleChange = (event: SelectChangeEvent<string>): void => {
        onChange(event.target.value);
    };

    const renderSelectedValue = (selectedValue: string): ReactNode => {
        if (renderValue !== undefined) {
            return renderValue(selectedValue);
        }

        return options.find(option => option.value === selectedValue)?.label ?? fallbackLabel;
    };

    return (
        <FormControl
            variant="standard"
            sx={formControlSx}
        >
            <Select<string>
                id={id}
                size="small"
                value={value}
                displayEmpty={displayEmpty}
                renderValue={renderSelectedValue}
                onChange={handleChange}
                sx={selectSx}
                disabled={disabled}
                MenuProps={{
                    MenuListProps: {
                        sx: {
                            paddingY: 0.25,
                        },
                    },
                    PaperProps: {
                        sx: {
                            width: menuWidth,
                            maxWidth: menuMaxWidth,
                            maxHeight: '415px',
                        },
                        TransitionProps: {
                            timeout: 0,
                        },
                    },
                }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={defaultMenuItemSx}
                        disabled={option.disabled}
                    >
                        {option.content ?? option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
